// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import InternalButton from '../button/internal';
import InternalIcon from '../icon/internal';
import { getBaseProps } from '../internal/base-component';
import { fireNonCancelableEvent } from '../internal/events';
import { useOneTheme } from '../internal/hooks/use-visual-mode';
import InternalPopover from '../popover/internal';
import InternalStatusIndicator from '../status-indicator/internal';
import styles from './styles.css.js';
import testStyles from './test-classes/styles.css.js';
const STATUS_ICON_VISIBLE_MS = 1500;
function useTransientFlag(durationMs) {
    const [active, setActive] = useState(false);
    const timerRef = useRef();
    const clear = useCallback(() => {
        clearTimeout(timerRef.current);
        setActive(false);
    }, []);
    const trigger = useCallback(() => {
        clearTimeout(timerRef.current);
        setActive(true);
        timerRef.current = setTimeout(() => setActive(false), durationMs);
    }, [durationMs]);
    useEffect(() => () => clearTimeout(timerRef.current), []);
    return { active, trigger, clear };
}
/**
 * Copy glyph and result glyph stacked in one icon slot so the swap can be animated
 */
function IconSwap({ status, showResult }) {
    return (React.createElement("span", { className: clsx(styles['icon-swap'], showResult && styles['icon-swap-copied']) },
        React.createElement(InternalIcon, { name: "copy", className: styles['icon-swap-copy'] }),
        React.createElement(InternalIcon, { name: status === 'error' ? 'status-negative' : 'status-positive', variant: status === 'error' ? 'error' : 'success', className: styles['icon-swap-result'] })));
}
export default function InternalCopyToClipboard({ variant = 'button', copyButtonAriaLabel, copyButtonText, copySuccessText, copyErrorText, textToCopy, textToDisplay, wrapText = true, popoverRenderWithPortal, disabled, disabledReason, onCopySuccess, onCopyFailure, __internalRootRef, ...restProps }) {
    const [status, setStatus] = useState('success');
    const [statusText, setStatusText] = useState(copySuccessText);
    const isOneTheme = useOneTheme();
    const result = useTransientFlag(STATUS_ICON_VISIBLE_MS);
    useEffect(() => {
        if (navigator.permissions) {
            navigator.permissions
                .query({ name: 'clipboard-write' })
                .then(result => {
                if (result.state === 'denied') {
                    setStatus('error');
                    setStatusText(copyErrorText);
                }
            })
                .catch(() => {
                // Permissions API not supported or failed.
            });
        }
    }, [copyErrorText]);
    const baseProps = getBaseProps(restProps);
    const reportResult = (nextStatus) => {
        setStatus(nextStatus);
        setStatusText(nextStatus === 'success' ? copySuccessText : copyErrorText);
        if (isOneTheme) {
            result.trigger();
        }
        fireNonCancelableEvent(nextStatus === 'success' ? onCopySuccess : onCopyFailure, { text: textToCopy });
    };
    const onClick = () => {
        if (!navigator.clipboard) {
            // The clipboard API is not available in insecure contexts.
            reportResult('error');
            return;
        }
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => reportResult('success'))
            .catch(() => reportResult('error'));
    };
    const triggerVariant = {
        button: 'normal',
        icon: 'icon',
        inline: 'inline-icon',
    }[variant];
    const isInline = variant === 'inline';
    const button = (React.createElement(InternalButton, { ariaLabel: copyButtonAriaLabel !== null && copyButtonAriaLabel !== void 0 ? copyButtonAriaLabel : copyButtonText, iconName: isOneTheme ? undefined : 'copy', iconSvg: isOneTheme ? React.createElement(IconSwap, { status: status, showResult: result.active }) : undefined, variant: triggerVariant, wrapText: false, formAction: "none", disabled: disabled, disabledReason: disabledReason }, copyButtonText));
    const oneThemePopoverProps = isOneTheme
        ? {
            __visible: result.active,
            __onVisibleChange: ({ detail }) => {
                if (!detail.visible) {
                    result.clear();
                }
            },
        }
        : {};
    const trigger = disabled ? (button) : (React.createElement(InternalPopover, { isInline: isInline, size: "medium", position: "top", triggerType: "custom", dismissButton: false, renderWithPortal: popoverRenderWithPortal, ...oneThemePopoverProps, content: isOneTheme ? (React.createElement("span", { className: styles['popover-message'] }, statusText)) : (React.createElement(InternalStatusIndicator, { type: status }, statusText)), __onOpen: onClick }, button));
    return (React.createElement("span", { ...baseProps, ref: __internalRootRef, className: clsx(baseProps.className, styles.root, testStyles.root) }, isInline ? (React.createElement("span", { className: clsx(styles['inline-container'], !wrapText && styles['inline-container-no-wrap']) },
        React.createElement("span", { className: styles['inline-container-trigger'] }, trigger),
        React.createElement("span", { className: clsx(testStyles['text-to-display'], testStyles['text-to-copy'], styles['text-to-display']) }, textToDisplay !== undefined ? textToDisplay : textToCopy))) : (trigger)));
}
//# sourceMappingURL=internal.js.map